"use client";
import React from "react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

interface FlozyLoaderProps {
  isLoading?: boolean;
  className?: string;
  size?: "sm" | "md" | "lg";
}

export function FlozyLoader({ 
  isLoading = false, 
  className = "", 
  size = "md" 
}: FlozyLoaderProps) {
  if (!isLoading) return null;

  const sizeConfig = {
    sm: { container: 80, logo: 48, strokeWidth: 3, radius: 36 },
    md: { container: 120, logo: 72, strokeWidth: 4, radius: 54 },
    lg: { container: 160, logo: 96, strokeWidth: 5, radius: 72 },
  };

  const config = sizeConfig[size];
  const circumference = 2 * Math.PI * config.radius;
  const segmentLength = circumference * 0.25; // 25% of circle
  
  // Offset to center the segment at 12 o'clock position
  // Positive offset moves the segment backwards (counterclockwise)
  // We need to shift by half the segment length to center it
  const centerOffset = segmentLength / 2;

  return (
    <div className={cn("flex items-center justify-center w-full h-full bg-background", className)}>
      <div className="flex flex-col items-center gap-8">
        
        {/* Loader Container */}
        <div 
          className="relative flex items-center justify-center"
          style={{ width: config.container, height: config.container }}
        >
          
          {/* Flozy Logo - Opacity Pulsing */}
          <motion.div 
            className="absolute inset-0 flex items-center justify-center z-10"
            animate={{
              opacity: [0.4, 1, 0.4],
            }}
            transition={{
              duration: 3,
              ease: "easeInOut",
              repeat: Infinity,
            }}
          >
            <img 
              src="/flozy.svg" 
              alt="Flozy" 
              style={{ width: config.logo, height: config.logo }}
              className="object-contain"
            />
          </motion.div>

          {/* Animated Circle Segment */}
          <svg
            className="absolute inset-0 w-full h-full -rotate-90"
            viewBox={`0 0 ${config.container} ${config.container}`}
            style={{ willChange: "transform" }}
          >
            {/* Background circle (optional - subtle track) */}
            <circle
              cx={config.container / 2}
              cy={config.container / 2}
              r={config.radius}
              fill="none"
              stroke="currentColor"
              strokeWidth={config.strokeWidth}
              className="text-muted-foreground/20"
            />

            {/* Animated segment - accelerates, decelerates, pauses at top centered */}
            <motion.circle
              cx={config.container / 2}
              cy={config.container / 2}
              r={config.radius}
              fill="none"
              stroke="currentColor"
              strokeWidth={config.strokeWidth}
              strokeLinecap="round"
              strokeDasharray={`${segmentLength} ${circumference - segmentLength}`}
              className="text-primary"
              animate={{
                strokeDashoffset: [
                  centerOffset,                      // Start: center at 12 o'clock
                  centerOffset - circumference,      // End: center back at 12 o'clock (full rotation)
                  centerOffset - circumference,      // Pause at top (centered)
                ],
              }}
              transition={{
                times: [0, 0.55, 1],
                duration: 2.5,
                ease: [0.65, 0, 0.35, 1], // Strong acceleration in the middle
                repeat: Infinity,
              }}
              style={{ willChange: "stroke-dashoffset" }}
            />
          </svg>

        </div>

        
      </div>
    </div>
  );
}
